import type { ChipProps } from '@mui/material/Chip';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import type { Theme, CSSObject, Components, ComponentsVariants } from '@mui/material/styles';

import { varAlpha } from 'minimal-shared/utils';

import SvgIcon from '@mui/material/SvgIcon';
import { chipClasses } from '@mui/material/Chip';

// ----------------------------------------------------------------------

/**
 * TypeScript (type definition and extension)
 * @to {@link file://./../../extend-theme-types.d.ts}
 */

export type ChipExtendVariant = {
  soft: true;
};

// ----------------------------------------------------------------------

/**
 * Icons
 */
/** https://icon-sets.iconify.design/solar/close-circle-bold/ */
const ChipDeleteIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <path
      fill="currentColor"
      fillRule="evenodd"
      d="M22 12c0 5.523-4.477 10-10 10S2 17.523 2 12S6.477 2 12 2s10 4.477 10 10M8.97 8.97a.75.75 0 0 1 1.06 0L12 10.94l1.97-1.97a.75.75 0 0 1 1.06 1.06L13.06 12l1.97 1.97a.75.75 0 0 1-1.06 1.06L12 13.06l-1.97 1.97a.75.75 0 0 1-1.06-1.06L10.94 12l-1.97-1.97a.75.75 0 0 1 0-1.06"
      clipRule="evenodd"
    />
  </SvgIcon>
);

// ----------------------------------------------------------------------

const COLORS = ['primary', 'secondary', 'info', 'success', 'warning', 'error'] as const;

type PaletteColor = (typeof COLORS)[number];

function styleColors(ownerState: ChipProps, styles: (val: PaletteColor) => CSSObject) {
  const outputStyle = COLORS.reduce((acc, color) => {
    if (!ownerState.disabled && ownerState.color === color) {
      acc = styles(color);
    }
    return acc;
  }, {});

  return outputStyle;
}

const softVariant: Record<string, ComponentsVariants<Theme>['MuiChip']> = {
  colors: COLORS.map((color) => ({
    props: ({ ownerState }) =>
      !ownerState.disabled && ownerState.variant === 'soft' && ownerState.color === color,
    style: ({ theme }) => ({
      color: theme.vars.palette[color].dark,
      backgroundColor: varAlpha(theme.vars.palette[color].mainChannel, 0.16),
      '&:hover': { backgroundColor: varAlpha(theme.vars.palette[color].mainChannel, 0.32) },
      ...theme.applyStyles('dark', {
        color: theme.vars.palette[color].light,
      }),
    }),
  })),
  inheritColor: [
    {
      props: ({ ownerState }) => ownerState.variant === 'soft' && ownerState.color === 'default',
      style: ({ theme }) => ({
        backgroundColor: varAlpha(theme.vars.palette.grey['500Channel'], 0.16),
        '&:hover': { backgroundColor: varAlpha(theme.vars.palette.grey['500Channel'], 0.32) },
      }),
    },
  ],
};

// ----------------------------------------------------------------------

/**
 * Variantes ADITIVAS do canvas (DESIGN-SYSTEM §8 T7). Nenhum default é trocado.
 *
 * Elas moram no `variants` do componente (e não dentro de `styleOverrides.root`)
 * porque o `createStyled` do MUI aplica `theme.components.MuiChip.variants`
 * DEPOIS de todos os slots de `styleOverrides` — é o único ponto em que o
 * `borderRadius: 999` vence o `sizeSmall` (8px) sem apagar o valor de ninguém.
 */
const canvasVariants: ComponentsVariants<Theme>['MuiChip'] = [
  /**
   * Alinha o `Chip` pequeno ao `Label`: parte das grades usa um, parte usa o outro.
   *
   * O peso e o padding horizontal moram no slot `label`, não na raiz:
   * - `styleOverrides.label` (abaixo, `fontWeight: fontWeightMedium`) escreve 500 no
   *   elemento que carrega o texto, e o 700 da raiz não venceria um valor declarado
   *   no próprio elemento;
   * - o `Chip` do MUI já dá `paddingLeft/Right: 8` ao `.MuiChip-labelSmall`
   *   (`node_modules/@mui/material/Chip/Chip.js:336-337`), então `padding:'0 10px'`
   *   na raiz SOMARIA e daria 18px por lado — o dobro dos 10px do `Label` que esta
   *   variante existe para igualar. Também desalinharia o `deleteIcon`, posicionado
   *   por margem negativa calibrada para raiz sem padding.
   */
  {
    props: { size: 'small' },
    style: {
      borderRadius: 999,
      height: 21,
      fontSize: 11,
      [`& .${chipClasses.label}`]: { fontWeight: 700, paddingLeft: 10, paddingRight: 10 },
    },
  },
  /**
   * `.filtpill` — ponte de migração enquanto o `DsFilterPill` não cobre as 36 telas.
   *
   * `height:'auto'` é obrigatório: um `<Chip variant="outlined" size="small"
   * color="default">` casa com as DUAS variantes, e o `height: 21` da primeira
   * (box-sizing border-box) neutralizaria o padding vertical de 5px daqui — a pílula
   * sairia com 21px em vez dos 5 + 11*1.5 + 5 = 26,5px do canvas. Mesma solução que
   * o `LabelRoot` já usa (`components/label/styles.tsx:94`).
   *
   * O padding horizontal volta para a raiz (12/6, o valor literal do `.filtpill`),
   * então o do slot `label` é zerado para não somar.
   */
  {
    props: { variant: 'outlined', size: 'small', color: 'default' },
    style: ({ theme }) => ({
      backgroundColor: theme.vars.palette.background.neutral,
      border: `1px solid ${theme.vars.palette.divider}`,
      borderRadius: 999,
      height: 'auto',
      fontSize: 11,
      color: theme.vars.palette.text.secondary,
      padding: '5px 6px 5px 12px',
      [`& .${chipClasses.label}`]: { fontWeight: 700, paddingLeft: 0, paddingRight: 0 },
    }),
  },
];

// ----------------------------------------------------------------------

const MuiChip: Components<Theme>['MuiChip'] = {
  /** **************************************
   * DEFAULT PROPS
   *************************************** */
  defaultProps: { deleteIcon: <ChipDeleteIcon /> },

  /** **************************************
   * VARIANTS (aditivas — §8 T7)
   *************************************** */
  variants: canvasVariants,

  /** **************************************
   * STYLE
   *************************************** */
  styleOverrides: {
    root: ({ ownerState, theme }) => {
      const styled = {
        colors: styleColors(ownerState, (color) => ({
          [`& .${chipClasses.avatar}`]: {
            color: theme.vars.palette[color].lighter,
            backgroundColor: theme.vars.palette[color].dark,
          },
        })),
        disabled: {
          [`&.${chipClasses.disabled}`]: {
            opacity: 1,
            [`& .${chipClasses.avatar}`]: {
              color: theme.vars.palette.action.disabled,
              backgroundColor: theme.vars.palette.action.disabledBackground,
            },
            ...(ownerState.variant === 'outlined' && {
              color: theme.vars.palette.action.disabled,
              borderColor: theme.vars.palette.action.disabledBackground,
            }),
            ...(['filled', 'soft'].includes(ownerState.variant!) && {
              color: theme.vars.palette.action.disabled,
              backgroundColor: theme.vars.palette.action.disabledBackground,
            }),
          },
        },
      };

      return {
        variants: [
          /**
           * @variant soft
           */
          softVariant.inheritColor,
          softVariant.colors,
        ].flat(),
        ...styled.colors,
        ...styled.disabled,
      };
    },
    label: ({ theme }) => ({ fontWeight: theme.typography.fontWeightMedium }),
    icon: { color: 'currentColor' },
    deleteIcon: {
      opacity: 0.48,
      color: 'currentColor',
      '&:hover': { opacity: 1, color: 'currentColor' },
    },
    /**
     * @sizes
     */
    sizeMedium: ({ theme }) => ({ borderRadius: theme.shape.borderRadius * 1.25 }),
    sizeSmall: ({ theme }) => ({ borderRadius: theme.shape.borderRadius }),
    /**
     * @variant filled
     */
    filled: ({ ownerState, theme }) => {
      const styled = {
        defaultColor: {
          ...(!ownerState.disabled &&
            ownerState.color === 'default' && {
              color: theme.vars.palette.common.white,
              backgroundColor: theme.vars.palette.text.primary,
              [`& .${chipClasses.avatar}`]: { color: theme.vars.palette.text.primary },
              '&:hover': { backgroundColor: theme.vars.palette.grey[700] },
              ...theme.applyStyles('dark', {
                color: theme.vars.palette.grey[800],
                '&:hover': { backgroundColor: theme.vars.palette.grey[100] },
              }),
            }),
        },
      };
      return { ...styled.defaultColor };
    },
    /**
     * @variant outlined
     */
    outlined: ({ ownerState, theme }) => {
      const styled = {
        defaultColor: {
          ...(!ownerState.disabled &&
            ownerState.color === 'default' && {
              borderColor: varAlpha(theme.vars.palette.grey['500Channel'], 0.32),
            }),
        },
      };
      return { ...styled.defaultColor };
    },
  },
};

// ----------------------------------------------------------------------

export const chip = { MuiChip };
